import React, { useEffect, useState, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  LinearProgress
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Visibility as VisibilityIcon,
  Mouse as MouseIcon,
  AttachMoney as MoneyIcon,
  Person as PersonIcon,
  Campaign as CampaignIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  DateRange as DateRangeIcon
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import axios from 'axios';
import { API_URL } from '../config';
import GoogleMCPNavigation from '../components/GoogleMCPNavigation';

interface AnalyticsData {
  overview: {
    total_impressions: number;
    total_clicks: number;
    total_cost: number;
    total_conversions: number;
    avg_ctr: number;
    avg_cpc: number;
    avg_cpm: number;
    conversion_rate: number;
  };
  trends: {
    date: string;
    impressions: number;
    clicks: number;
    cost: number;
    conversions: number;
    ctr: number;
    cpc: number;
  }[];
  campaigns_performance: {
    campaign_name: string;
    impressions: number;
    clicks: number;
    cost: number;
    conversions: number;
    ctr: number;
    cpc: number;
    conversion_rate: number;
  }[];
  keywords_performance: {
    keyword: string;
    impressions: number;
    clicks: number;
    cost: number;
    conversions: number;
    ctr: number;
    cpc: number;
    avg_position: number;
  }[];
  device_breakdown: {
    device: string;
    impressions: number;
    clicks: number;
    conversions: number;
    percentage: number;
  }[];
  location_performance: {
    location: string;
    impressions: number;
    clicks: number;
    conversions: number;
    cost: number;
  }[];
}

const GoogleMCPAnalytics: React.FC = () => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState('30d');
  const [selectedMetric, setSelectedMetric] = useState('impressions');
  const [comparisonPeriod, setComparisonPeriod] = useState('previous');

  const dateRangeOptions = [
    { value: '7d', label: 'Últimos 7 dias' },
    { value: '30d', label: 'Últimos 30 dias' },
    { value: '90d', label: 'Últimos 90 dias' },
    { value: 'custom', label: 'Período personalizado' }
  ];

  const metricOptions = [
    { value: 'impressions', label: 'Impressões', color: '#8884d8' },
    { value: 'clicks', label: 'Cliques', color: '#82ca9d' },
    { value: 'cost', label: 'Custo', color: '#ffc658' },
    { value: 'conversions', label: 'Conversões', color: '#ff7300' }
  ];

  const deviceColors = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  const fetchAnalyticsData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/google-mcp/analytics/performance`, {
        params: {
          date_range: dateRange,
          comparison: comparisonPeriod
        }
      });
      setAnalyticsData(response.data.data);
    } catch (error: any) {
      console.error('Erro ao carregar analytics:', error);
      // Dados simulados para desenvolvimento
      setAnalyticsData({
        overview: {
          total_impressions: 125430,
          total_clicks: 3876,
          total_cost: 9850.75,
          total_conversions: 187,
          avg_ctr: 3.09,
          avg_cpc: 2.54,
          avg_cpm: 78.50,
          conversion_rate: 4.83
        },
        trends: [
          { date: '2024-01-01', impressions: 4200, clicks: 130, cost: 330, conversions: 6, ctr: 3.1, cpc: 2.54 },
          { date: '2024-01-02', impressions: 4500, clicks: 145, cost: 368, conversions: 8, ctr: 3.2, cpc: 2.54 },
          { date: '2024-01-03', impressions: 3800, clicks: 115, cost: 292, conversions: 5, ctr: 3.0, cpc: 2.54 },
          { date: '2024-01-04', impressions: 5100, clicks: 165, cost: 419, conversions: 9, ctr: 3.2, cpc: 2.54 },
          { date: '2024-01-05', impressions: 4800, clicks: 152, cost: 386, conversions: 7, ctr: 3.2, cpc: 2.54 },
          { date: '2024-01-06', impressions: 4300, clicks: 138, cost: 350, conversions: 6, ctr: 3.2, cpc: 2.54 },
          { date: '2024-01-07', impressions: 4600, clicks: 148, cost: 376, conversions: 8, ctr: 3.2, cpc: 2.54 }
        ],
        campaigns_performance: [
          {
            campaign_name: 'Consórcio Carros - Geral',
            impressions: 75250,
            clicks: 2387,
            cost: 6050.45,
            conversions: 115,
            ctr: 3.17,
            cpc: 2.54,
            conversion_rate: 4.82
          },
          {
            campaign_name: 'Consórcio Imóveis - Premium',
            impressions: 50180,
            clicks: 1489,
            cost: 3800.30,
            conversions: 72,
            ctr: 2.97,
            cpc: 2.55,
            conversion_rate: 4.84
          }
        ],
        keywords_performance: [
          {
            keyword: 'consórcio carros',
            impressions: 25430,
            clicks: 812,
            cost: 2065,
            conversions: 39,
            ctr: 3.19,
            cpc: 2.54,
            avg_position: 2.1
          },
          {
            keyword: 'consórcio imóveis',
            impressions: 18750,
            clicks: 558,
            cost: 1420,
            conversions: 27,
            ctr: 2.98,
            cpc: 2.55,
            avg_position: 2.3
          },
          {
            keyword: 'financiamento carro',
            impressions: 15680,
            clicks: 485,
            cost: 1234,
            conversions: 23,
            ctr: 3.09,
            cpc: 2.54,
            avg_position: 2.5
          }
        ],
        device_breakdown: [
          { device: 'Mobile', impressions: 62715, clicks: 1938, conversions: 93, percentage: 50 },
          { device: 'Desktop', impressions: 50172, clicks: 1551, conversions: 75, percentage: 40 },
          { device: 'Tablet', impressions: 12543, clicks: 387, conversions: 19, percentage: 10 }
        ],
        location_performance: [
          { location: 'São Paulo', impressions: 45160, clicks: 1395, conversions: 67, cost: 3540 },
          { location: 'Rio de Janeiro', impressions: 28750, clicks: 889, conversions: 43, cost: 2260 },
          { location: 'Minas Gerais', impressions: 22580, clicks: 698, conversions: 34, cost: 1775 },
          { location: 'Paraná', impressions: 15940, clicks: 493, conversions: 24, cost: 1252 },
          { location: 'Outros', impressions: 13000, clicks: 401, conversions: 19, cost: 1020 }
        ]
      });
    }
    setLoading(false);
  }, [dateRange, comparisonPeriod]);

  useEffect(() => {
    fetchAnalyticsData();
  }, [dateRange, fetchAnalyticsData]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  const getChangeIcon = (change: number) => {
    if (change > 0) {
      return <TrendingUpIcon sx={{ color: 'success.main', fontSize: 16 }} />;
    } else if (change < 0) {
      return <TrendingDownIcon sx={{ color: 'error.main', fontSize: 16 }} />;
    }
    return null;
  };

  const getChangeColor = (change: number) => {
    if (change > 0) return 'success.main';
    if (change < 0) return 'error.main';
    return 'text.secondary';
  };

  const exportReport = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/google-mcp/analytics/export`, {
        params: { date_range: dateRange },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analytics_report_${dateRange}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error: any) {
      console.error('Erro ao exportar relatório:', error);
    }
  };

  if (loading && !analyticsData) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      {/* Navegação */}
      <GoogleMCPNavigation currentPage="analytics" />
      
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Analytics Google MCP
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchAnalyticsData}
              disabled={loading}
            >
              Atualizar
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={exportReport}
            >
              Exportar Relatório
            </Button>
          </Box>
        </Box>
        <Typography variant="subtitle1" color="text.secondary">
          Análise detalhada de performance das campanhas Google MCP
        </Typography>
      </Box>

      {/* Filtros */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Período</InputLabel>
              <Select
                value={dateRange}
                label="Período"
                onChange={(e) => setDateRange(e.target.value)}
                startAdornment={<DateRangeIcon sx={{ mr: 1 }} />}
              >
                {dateRangeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Métrica Principal</InputLabel>
              <Select
                value={selectedMetric}
                label="Métrica Principal"
                onChange={(e) => setSelectedMetric(e.target.value)}
              >
                {metricOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Comparar com</InputLabel>
              <Select
                value={comparisonPeriod}
                label="Comparar com"
                onChange={(e) => setComparisonPeriod(e.target.value)}
              >
                <MenuItem value="previous">Período anterior</MenuItem>
                <MenuItem value="last_year">Mesmo período ano passado</MenuItem>
                <MenuItem value="none">Sem comparação</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Dados atualizados em: {new Date().toLocaleString('pt-BR')}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {analyticsData && (
        <>
          {/* Métricas Principais */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4">
                        {formatNumber(analyticsData.overview.total_impressions)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total de Impressões
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        {getChangeIcon(5.2)}
                        <Typography variant="body2" color={getChangeColor(5.2)} sx={{ ml: 0.5 }}>
                          +5.2% vs período anterior
                        </Typography>
                      </Box>
                    </Box>
                    <VisibilityIcon color="primary" sx={{ fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4">
                        {formatNumber(analyticsData.overview.total_clicks)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total de Cliques
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        {getChangeIcon(8.7)}
                        <Typography variant="body2" color={getChangeColor(8.7)} sx={{ ml: 0.5 }}>
                          +8.7% vs período anterior
                        </Typography>
                      </Box>
                    </Box>
                    <MouseIcon color="success" sx={{ fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4">
                        {formatCurrency(analyticsData.overview.total_cost)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Custo Total
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        {getChangeIcon(-2.1)}
                        <Typography variant="body2" color={getChangeColor(-2.1)} sx={{ ml: 0.5 }}>
                          -2.1% vs período anterior
                        </Typography>
                      </Box>
                    </Box>
                    <MoneyIcon color="warning" sx={{ fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4">
                        {formatNumber(analyticsData.overview.total_conversions)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Conversões
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        {getChangeIcon(12.3)}
                        <Typography variant="body2" color={getChangeColor(12.3)} sx={{ ml: 0.5 }}>
                          +12.3% vs período anterior
                        </Typography>
                      </Box>
                    </Box>
                    <PersonIcon color="info" sx={{ fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Métricas Secundárias */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="primary">
                    {formatPercentage(analyticsData.overview.avg_ctr)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    CTR Médio
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="success.main">
                    {formatCurrency(analyticsData.overview.avg_cpc)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    CPC Médio
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="warning.main">
                    {formatCurrency(analyticsData.overview.avg_cpm)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    CPM Médio
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="info.main">
                    {formatPercentage(analyticsData.overview.conversion_rate)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Taxa de Conversão
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Gráfico de Tendências */}
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Tendências de Performance
            </Typography>
            <Box sx={{ height: 400, mt: 2 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={analyticsData.trends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(value) => new Date(value).toLocaleDateString('pt-BR', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis />
                  <RechartsTooltip 
                    labelFormatter={(value) => new Date(value).toLocaleDateString('pt-BR')}
                    formatter={(value: any, name: string) => {
                      if (name === 'cost') return [formatCurrency(value), 'Custo'];
                      if (name === 'ctr' || name === 'cpc') return [value.toFixed(2), name.toUpperCase()];
                      return [formatNumber(value), name.charAt(0).toUpperCase() + name.slice(1)];
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey={selectedMetric} 
                    stroke={metricOptions.find(m => m.value === selectedMetric)?.color || '#8884d8'} 
                    strokeWidth={3}
                    dot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          {/* Performance por Dispositivo */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Performance por Dispositivo
                </Typography>
                <Box sx={{ height: 300, mt: 2 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={analyticsData.device_breakdown}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ device, percentage }) => `${device} (${percentage}%)`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="impressions"
                      >
                        {analyticsData.device_breakdown.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={deviceColors[index % deviceColors.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip formatter={(value: any) => [formatNumber(value), 'Impressões']} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Performance por Localização
                </Typography>
                <Box sx={{ height: 300, mt: 2 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analyticsData.location_performance}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="location" />
                      <YAxis />
                      <RechartsTooltip formatter={(value: any) => [formatNumber(value), 'Impressões']} />
                      <Bar dataKey="impressions" fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Performance de Campanhas */}
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Performance de Campanhas
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Campanha</TableCell>
                    <TableCell align="right">Impressões</TableCell>
                    <TableCell align="right">Cliques</TableCell>
                    <TableCell align="right">CTR</TableCell>
                    <TableCell align="right">CPC</TableCell>
                    <TableCell align="right">Custo</TableCell>
                    <TableCell align="right">Conversões</TableCell>
                    <TableCell align="right">Taxa Conv.</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analyticsData.campaigns_performance.map((campaign, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CampaignIcon sx={{ mr: 1, color: 'primary.main' }} />
                          {campaign.campaign_name}
                        </Box>
                      </TableCell>
                      <TableCell align="right">{formatNumber(campaign.impressions)}</TableCell>
                      <TableCell align="right">{formatNumber(campaign.clicks)}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                          {formatPercentage(campaign.ctr)}
                          <LinearProgress
                            variant="determinate"
                            value={campaign.ctr}
                            sx={{ width: 50, ml: 1, height: 6, borderRadius: 3 }}
                            color={campaign.ctr > 3 ? 'success' : campaign.ctr > 2 ? 'warning' : 'error'}
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="right">{formatCurrency(campaign.cpc)}</TableCell>
                      <TableCell align="right">{formatCurrency(campaign.cost)}</TableCell>
                      <TableCell align="right">{formatNumber(campaign.conversions)}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={formatPercentage(campaign.conversion_rate)}
                          color={campaign.conversion_rate > 5 ? 'success' : campaign.conversion_rate > 3 ? 'warning' : 'default'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Performance de Palavras-chave */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Top Palavras-chave
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Palavra-chave</TableCell>
                    <TableCell align="right">Impressões</TableCell>
                    <TableCell align="right">Cliques</TableCell>
                    <TableCell align="right">CTR</TableCell>
                    <TableCell align="right">CPC</TableCell>
                    <TableCell align="right">Custo</TableCell>
                    <TableCell align="right">Conversões</TableCell>
                    <TableCell align="right">Pos. Média</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analyticsData.keywords_performance.map((keyword, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <SearchIcon sx={{ mr: 1, color: 'secondary.main' }} />
                          <Chip label={keyword.keyword} variant="outlined" size="small" />
                        </Box>
                      </TableCell>
                      <TableCell align="right">{formatNumber(keyword.impressions)}</TableCell>
                      <TableCell align="right">{formatNumber(keyword.clicks)}</TableCell>
                      <TableCell align="right">{formatPercentage(keyword.ctr)}</TableCell>
                      <TableCell align="right">{formatCurrency(keyword.cpc)}</TableCell>
                      <TableCell align="right">{formatCurrency(keyword.cost)}</TableCell>
                      <TableCell align="right">{formatNumber(keyword.conversions)}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={keyword.avg_position.toFixed(1)}
                          color={keyword.avg_position <= 2 ? 'success' : keyword.avg_position <= 4 ? 'warning' : 'default'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Container>
  );
};

export default GoogleMCPAnalytics;